import {
  Job as JobProto,
  MemberRoleType,
  MemberType,
  SearchableJob as SearchableJobProto,
  WaitingOn as WaitingOnProto,
  SearchJobsRequest,
  SearchJobScopeOption,
  MilestoneTask as MilestoneTaskProto,
} from '@taxfyle/api-internal/internal/job_pb'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import { PrimeTeam } from 'iam/TeamStore'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import { collection } from 'infra/collection'
import { getFeatureToggleClient } from 'infra/featureToggles'
import { WorkAPI } from 'jobs/API'
import { FindJobsQuery, JobScopeOption } from 'jobs/dtos/job'
import Job, { WaitingOn } from 'jobs/Job'
import SearchableJob, { SearchableMember } from 'jobs/SearchableJob'
import { PrimePublishedLegend } from 'legends/PublishedLegendStore'
import { PrimeConversation } from 'messaging/ConversationStore'
import { Observable } from 'rxjs'
import { filter, map } from 'rxjs/operators'
import { CursorPaged, Id } from 'types/definitions'
import { match } from 'utils/pattern-match-util'
import { filterNonNil } from 'utils/rx-util'
import { GetTeamMember } from 'iam/TeamMemberStore'
import { GetCurrentMember } from 'iam/SessionStore'
import TypeCase = RealtimeEvent.TypeCase
import { BoolValue } from 'google-protobuf/google/protobuf/wrappers_pb'

export default class JobSearchStore {
  /**
   * Searchable jobs collection.
   */
  searchableJobs = collection<SearchableJobProto.AsObject, SearchableJob>({
    create: () =>
      new SearchableJob(
        this.primeWorkspace,
        this.primeConversation,
        this.primePublishedLegend,
        this.getCurrentUserId,
        this.getTeamMember,
        this.getCurrentMember,
      ),
  })

  readonly jobEvents$: Observable<JobSearchEvent> = this.events$.pipe(
    // Only events relating to jobs.
    filter((event) => {
      switch (event.getTypeCase()) {
        case TypeCase.JOB_CREATED:
        case TypeCase.JOB_UPDATED:
          return true
      }
      return false
    }),
    map((e) => this.mapJobRealtimeEvent(e)),
    filterNonNil(),
  )

  constructor(
    private api: WorkAPI,
    private primeWorkspace: PrimeWorkspace,
    private primeConversation: PrimeConversation,
    private primePublishedLegend: PrimePublishedLegend,
    private primeTeam: PrimeTeam,
    private getCurrentUserId: () => string | null,
    private getTeamMember: GetTeamMember,
    private getCurrentMember: GetCurrentMember,
    private events$: Observable<RealtimeEvent>,
    private localJobChanges$: Observable<Job>,
  ) {
    this.localJobChanges$.subscribe((job) => {
      let searchableJob = this.searchableJobs.get(job.id)

      if (!searchableJob) {
        searchableJob = this.searchableJobs.set({ id: Id.assert(job.id) })
      }

      searchableJob.set(this.mapJobToSearchableJob(job))
    })
  }

  /**
   * Gets the total size from the `SearchJobResponse`.
   *
   * @param query
   */
  async getTotal(query: FindJobsQuery): Promise<number> {
    const req = new SearchJobsRequest()
    if (query.$limit) {
      req.setPageSize(query.$limit)
    }
    if (query.after) {
      req.setPageToken(query.after)
    }

    if (query.orderBy) {
      req.setOrderBy(query.orderBy)
    }

    if (query.searchText) {
      req.setSearchText(query.searchText)
    }

    if (query.filter) {
      req.setFilter(query.filter)
    }

    req.setScope(
      match(query.scope, {
        [JobScopeOption.All]: () => SearchJobScopeOption.ALL,
        [JobScopeOption.Mine]: () => SearchJobScopeOption.MINE,
      }),
    )

    const result = await this.api.jobsV3.searchJobs(req)

    return result.getTotalSize()
  }

  /**
   * Searches jobs.
   *
   * @param query
   */
  async searchJobs(query: FindJobsQuery): Promise<CursorPaged<SearchableJob>> {
    const req = new SearchJobsRequest()
    if (query.$limit) {
      req.setPageSize(query.$limit)
    }
    if (query.after) {
      req.setPageToken(query.after)
    }

    if (query.orderBy) {
      req.setOrderBy(query.orderBy)
    }

    if (query.searchText) {
      req.setSearchText(query.searchText)
    }

    if (query.filter) {
      req.setFilter(query.filter)
    }

    req.setScope(
      match(query.scope, {
        [JobScopeOption.All]: () => SearchJobScopeOption.ALL,
        [JobScopeOption.Mine]: () => SearchJobScopeOption.MINE,
      }),
    )

    const result = await this.api.jobsV3.searchJobs(req)

    return CursorPaged.map(
      {
        data: result.getJobsList().map((op) => op.toObject()),
        total: result.getTotalSize(),
        cursor: result.getNextPageToken(),
      },
      this.searchableJobs.set,
    )
  }

  /**
   * Maps the proto message to an event type.
   *
   * @param e
   * @private
   */
  private mapJobRealtimeEvent(e: RealtimeEvent): JobSearchEvent | null {
    switch (e.getTypeCase()) {
      case TypeCase.JOB_UPDATED: {
        const job = e.getJobUpdated()?.getJob()?.toObject()
        if (!job) {
          return null
        }

        return {
          type: 'JobUpdated',
          job: this.searchableJobs.set(
            this.mapJobProtoToSearchableJobProto(job),
          ),
        }
      }
      case TypeCase.JOB_CREATED: {
        const job = e.getJobCreated()?.getJob()?.toObject()
        if (!job) {
          return null
        }

        return {
          type: 'JobCreated',
          job: this.searchableJobs.set(
            this.mapJobProtoToSearchableJobProto(job),
          ),
        }
      }
    }
    return null
  }

  private mapJobProtoToSearchableJobProto(
    json: JobProto.AsObject,
  ): SearchableJobProto.AsObject {
    const members = json.membersList.map((m) => {
      return {
        id: m.id,
        teamId: m.teamId,
        displayName: m.displayName,
        picture: m.picture,
        type: match(m.type, {
          [JobProto.MemberType.CLIENT]: () => MemberType.MEMBER_TYPE_CLIENT,
          [JobProto.MemberType.PROVIDER]: () => MemberType.MEMBER_TYPE_PROVIDER,
        }),
        role: match(m.role, {
          [JobProto.MemberRole.CHAMPION]: () => MemberRoleType.CHAMPION,
          [JobProto.MemberRole.SUPPORTING]: () => MemberRoleType.SUPPORTING,
        }),
      }
    })
    const clientTeamId = members.find(
      (m) => m.teamId && m.type === MemberType.MEMBER_TYPE_CLIENT,
    )?.teamId
    const proTeamId = members.find(
      (m) => m.teamId && m.type === MemberType.MEMBER_TYPE_PROVIDER,
    )?.teamId

    return {
      legendName: json.legend?.name ?? '',
      legendId: json.legend?.id ?? '',
      legendVersion: json.legend?.version ?? 0,
      total: json.total,
      jobScopeItemsList: json.jobScopeItemsList.map((i) => i.name),
      isRequestedPro: json.requestedProPublicId
        ? new BoolValue().setValue(true).toObject()
        : undefined,
      ratingSubmitted: json.ratingSubmitted,
      dateDeadline: json.dateDeadline,
      dateCreated: json.dateCreated,
      resolution: json.resolution,
      dateSubmitted: json.dateTransmitted,
      dateClaimed: json.dateAccepted,
      dateCompleted: json.dateClosed,
      dateModified: json.dateModified,
      dateLastActivity: json.dateLastActivity,
      id: json.id,
      workspaceId: json.workspaceId,
      shortId: json.shortId,
      title: json.name?.value ?? '',
      description: json.description?.value || '', // The computed job title
      status: json.status,
      milestonesList:
        json.milestonesList?.map((m) => ({
          id: m.id,
          title: m.title,
          completed: m.completed,
          tasksList: m.tasksList.map((t) => ({
            id: t.id,
            title: t.title,
            completed: t.completed,
            dateCompleted: t.dateCompleted,
            completedByPublicId: t.completedByPublicId,
            designee: match(t.designee, {
              [JobProto.Milestone.MilestoneTask.TaskDesignee.CLIENT]: () =>
                MilestoneTaskProto.TaskDesignee.CLIENT,
              [JobProto.Milestone.MilestoneTask.TaskDesignee.PROVIDER]: () =>
                MilestoneTaskProto.TaskDesignee.PROVIDER,
              [match.DEFAULT]: () =>
                MilestoneTaskProto.TaskDesignee.TASK_DESIGNEE_UNSPECIFIED,
            }),
          })),
        })) ?? [],
      waitingOn: json.waitingOn,
      conversationId: json.conversationId,
      membersList: members,
      clientTeamId: clientTeamId ?? '',
      proTeamId: proTeamId ?? '',
      clientTeamName: this.primeTeam({ id: clientTeamId })?.name ?? '',
      proTeamName: this.primeTeam({ id: proTeamId })?.name ?? '',
      workspaceName: this.primeWorkspace({ id: json.workspaceId })?.name ?? '',
      jobSpecsList: json.jobSpecsList,
      providerCutAmount: json.providerCutAmount,
      archived: json.archived,
      progressionStep: json.progressionStep,
      scopeChangeStatus: json.scopeChangeStatus,
      isExtensionOnlyJob: json.type?.tax?.isExtensionOnlyJob ?? false,
    }
  }

  private mapJobToSearchableJob(job: Job): Partial<SearchableJob> {
    const members: SearchableMember[] = job.collaborators.map((c) => {
      return {
        id: Id.assert(c.member.user.publicId),
        teamId: c.team?.id,
        displayName: c.member.displayName,
        avatar: c.member.picture,
        avatarFallback: c.member.avatarFallback,
        type: c.type,
        role: c.role,
        isCurrentUser: c.member.isCurrentUser,
      }
    })

    const waitingOn = job.waitingOn
      ? match(job.waitingOn, {
          [WaitingOn.Provider]: () => WaitingOnProto.WAITING_ON_PRO,
          [WaitingOn.Client]: () => WaitingOnProto.WAITING_ON_CLIENT,
        })
      : WaitingOnProto.WAITING_ON_UNSPECIFIED

    const milestones = job.milestones.map((m) => {
      return {
        id: m.id,
        title: m.title,
        completed: m.completed,
        tasksList: m.tasks.map((t) => {
          return {
            id: t.id,
            completed: t.completed,
            dateCompleted: t.completedAt,
          }
        }),
      }
    })

    return {
      shortId: Id.assert(job.shortId),
      workspace: job.workspace,
      legendName: job.legendName,
      dateDeadline: job.dateDeadline,
      dateCreated: job.dateCreated,
      dateSubmitted: job.dateAccepted,
      dateAccepted: job.dateAccepted,
      dateClosed: job.dateClosed,
      dateLastActivity: job.dateLastActivity,
      resolution: job.resolution,
      name: job.displayName,
      status: job.status,
      waitingOn,
      conversation: job.conversation,
      clientTeamName: job.clientTeamName,
      proTeamName: job.proTeamName,
      providerCutAmount: job.providerCutAmount,
      members,
      milestones,
    }
  }
}

// This can later be abstracted to be used for all job events.
export type JobSearchEvent = JobUpdatedEvent | JobCreatedEvent

/**
 * Job was updated.
 */
export interface JobUpdatedEvent {
  type: 'JobUpdated'
  job: SearchableJob
}

/**
 * Job was removed.
 */
export interface JobCreatedEvent {
  type: 'JobCreated'
  job: SearchableJob
}
